import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import { API_URL } from '../../common/Constants';
import Cookies from '../../common/Cookies';

const Alert = MuiAlert;

const DEFAULT_CAL_OAUTH_CALLBACK_URL =
  'https://app.chattitude.ai/slickbots-ws/api/integrations/oauth/callback';

function calcomOAuthCallbackDisplayUrl(resellerBaseUrl) {
  const t = (resellerBaseUrl || '').trim().replace(/\/+$/, '');
  if (!t) return DEFAULT_CAL_OAUTH_CALLBACK_URL;
  return `${t}/slickbots-ws/api/integrations/oauth/callback`;
}

const CALCOM_OAUTH_POLL_MS = 2000;
const CALCOM_OAUTH_POLL_MAX_MS = 5 * 60 * 1000;

const CalcomModal = ({ open, onClose, productName = 'Chattitude', resellerBaseUrl }) => {
  const [oauthCallbackDisplay, setOauthCallbackDisplay] = useState('');
  const [credentials, setCredentials] = useState({
    clientId: '',
    clientSecret: '',
    defaultUsername: '',
  });
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [loading, setLoading] = useState(false);
  const connectPollRef = useRef(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);

  const authHeader = () => {
    const jwtToken = Cookies.getJwtToken();
    if (jwtToken) return { Authorization: `Bearer ${jwtToken}` };
    const tenantId = Cookies.getTenantId();
    return tenantId ? { Authorization: String(tenantId) } : {};
  };

  useEffect(() => {
    const fetchCredentials = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_URL}/integrations/calcom/credentials`, { headers: authHeader() });
        setCredentials({
          clientId: res.data?.clientId || '',
          clientSecret: res.data?.clientSecret || '',
          defaultUsername: res.data?.defaultUsername || '',
        });
      } catch (e) {
        setCredentials({
          clientId: '',
          clientSecret: '',
          defaultUsername: '',
        });
        setError('Failed to fetch credentials.');
      } finally {
        setLoading(false);
      }
    };

    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API_URL}/integrations/calcom/status`, { headers: authHeader() });
        setConnected(!!res.data?.connected);
      } catch (e) {
        setConnected(false);
      }
    };

    const fetchOAuthCallbackUrl = async () => {
      try {
        const res = await axios.get(`${API_URL}/integrations/calcom/oauth-callback-url`, { headers: authHeader() });
        if (res.data?.redirectUri) {
          setOauthCallbackDisplay(res.data.redirectUri);
        } else {
          setOauthCallbackDisplay(calcomOAuthCallbackDisplayUrl(resellerBaseUrl));
        }
      } catch (e) {
        setOauthCallbackDisplay(calcomOAuthCallbackDisplayUrl(resellerBaseUrl));
      }
    };

    if (open) {
      fetchCredentials();
      fetchStatus();
      fetchOAuthCallbackUrl();
      setSnackbar({ open: false, message: '', severity: 'success' });
    }

    return () => {
      if (connectPollRef.current) {
        clearInterval(connectPollRef.current);
        connectPollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resellerBaseUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!credentials.clientId) return 'Client ID is required.';
    if (!credentials.clientSecret) return 'Client Secret is required.';
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/integrations/calcom/credentials`, credentials, { headers: authHeader() });
      setSnackbar({ open: true, message: 'Credentials saved successfully!', severity: 'success' });
      setTimeout(() => onClose && onClose(), 1200);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to save credentials.', severity: 'error' });
      setError('Failed to save credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    const validationError = validate();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }
    setConnecting(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/integrations/calcom/credentials`, credentials, { headers: authHeader() });

      const res = await axios.get(`${API_URL}/integrations/calcom/authorize-url`, { headers: authHeader() });
      const url = res.data?.url;
      if (!url) throw new Error('Missing authorize URL');

      if (connectPollRef.current) {
        clearInterval(connectPollRef.current);
        connectPollRef.current = null;
      }

      window.open(url, '_blank', 'noopener,noreferrer');

      const started = Date.now();
      const poll = setInterval(async () => {
        if (Date.now() - started > CALCOM_OAUTH_POLL_MAX_MS) {
          if (connectPollRef.current) {
            clearInterval(connectPollRef.current);
            connectPollRef.current = null;
          }
          setConnecting(false);
          setSnackbar({
            open: true,
            message: 'Timed out waiting for Cal.com. If you finished in the other tab, close this message and check status.',
            severity: 'warning',
          });
          return;
        }
        try {
          const statusRes = await axios.get(`${API_URL}/integrations/calcom/status`, { headers: authHeader() });
          if (statusRes.data?.connected) {
            if (connectPollRef.current) {
              clearInterval(connectPollRef.current);
              connectPollRef.current = null;
            }
            setConnected(true);
            setConnecting(false);
            setSnackbar({ open: true, message: 'Cal.com connected!', severity: 'success' });
          }
        } catch (e) {
          // ignore while polling
        }
      }, CALCOM_OAUTH_POLL_MS);
      connectPollRef.current = poll;
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to initiate Cal.com OAuth.', severity: 'error' });
      setError('Failed to initiate Cal.com OAuth.');
      setConnecting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      sx={{ zIndex: 99999 }}
      PaperProps={{ style: { minWidth: 900, minHeight: 600, zIndex: 99999 } }}
    >
      <DialogTitle sx={{ position: 'relative', pr: 6 }}>
        Cal.com (OAuth)
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: '#888', background: '#f5f5f5', zIndex: 21000 }}
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="OAuth Client ID"
              name="clientId"
              value={credentials.clientId}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="OAuth Client Secret"
              name="clientSecret"
              value={credentials.clientSecret}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Default Cal.com username (handle)"
              name="defaultUsername"
              value={credentials.defaultUsername}
              onChange={handleChange}
              fullWidth
              helperText="Your public Cal.com handle (e.g. acme in cal.com/acme). Used for slot lookups by slug and the booking widget; meeting types are chosen in chat from your live event list."
            />

            <Box sx={{ mt: 2, mb: 2, p: 2, background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Connect Cal.com
              </Typography>
              <ol style={{ paddingLeft: 18, marginBottom: 8 }}>
                <li>
                  In{' '}
                  <a href="https://app.cal.com/settings/developer/oauth" target="_blank" rel="noopener noreferrer">
                    Cal.com Developer → OAuth clients
                  </a>
                  , create a client for {productName}.
                </li>
                <li>
                  Add this <b>exact</b> redirect URI to the Cal.com OAuth client:
                  <ul style={{ marginTop: 4 }}>
                    <li>
                      <b>{oauthCallbackDisplay || calcomOAuthCallbackDisplayUrl(resellerBaseUrl)}</b>
                    </li>
                  </ul>
                </li>
                <li>
                  Enable the same API scopes on the Cal.com OAuth client as in your integration DB (
                  <code>default_scopes</code> for <code>calcom</code>). If you see &quot;scope exceeds&quot;, reduce DB scopes or add
                  permissions in Cal.com.
                </li>
                <li>
                  <b>Before Connect:</b> sign in at{' '}
                  <a href="https://app.cal.com" target="_blank" rel="noopener noreferrer">
                    app.cal.com
                  </a>{' '}
                  in this browser. Pending OAuth clients can only be authorized by the client owner until Cal.com approves the app.
                </li>
                <li>
                  <b>Connect</b> opens Cal.com in a <b>new tab</b> so you stay in {productName}. Complete consent in that tab; this
                  dialog will show Connected when the server receives the callback. Sign in at app.cal.com first if you otherwise land on
                  the Cal.com home page (known Cal.com login redirect issue).
                </li>
                <li>Set default username so slot and widget URLs resolve; visitors pick the meeting type in chat from your Cal.com event types.</li>
                <li>Paste Client ID and Client Secret above, then Connect.</li>
                <li>After connecting, attach Cal.com to your agent under Tools Integrations.</li>
              </ol>
            </Box>

            {error && <Typography color="error" variant="body2">{error}</Typography>}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', gap: 2, py: 2, px: 3 }}>
        <Button
          onClick={handleSave}
          variant="outlined"
          color="primary"
          disabled={loading || connecting}
          sx={{ minWidth: 180, py: 1.2 }}
        >
          Save &amp; Close
        </Button>
        <Button
          onClick={handleConnect}
          variant="contained"
          color="success"
          disabled={connecting || loading || !credentials.clientId || !credentials.clientSecret}
          sx={{ minWidth: 220, py: 1.2 }}
        >
          {connected ? 'Connected to Cal.com' : connecting ? 'Connecting...' : 'Connect to Cal.com'}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default CalcomModal;
